import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LukosAIHero, LukosAIFeatures, LukosAIStrategy } from '../../components/custom/LukosAIPremium/LukosAIPremium';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Sparkles, ExternalLink } from 'lucide-react';
import { LukIAMockup } from '../../components/custom/LukosEcommerceShowcase/LukosPlatformMockups';
import '../../components/custom/LukosEcommerceShowcase/LukosEcommerceShowcase.css';


const LukosAIPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white pt-24">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <LukosAIHero />

        <section className="bg-white px-6 pb-20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-3 tracking-tight">
              Veja o ERP operado pelo Claude e pelo ChatGPT
            </h2>
            <p className="text-slate-500 text-lg mb-8 max-w-2xl mx-auto">
              Consultas e tarefas no ERP Lukos em linguagem natural, com permissões e confirmação antes de qualquer alteração.
            </p>
            <div className="relative w-full overflow-hidden rounded-3xl shadow-2xl shadow-slate-900/10 border border-slate-200 aspect-video bg-slate-900">
              <iframe
                src="https://www.youtube.com/embed/QzDi12Q8W6I?start=702&rel=0&modestbranding=1"
                title="ERP Lukos operado pelo Claude e pelo ChatGPT"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        </section>

        <section className="bg-slate-50 px-6 py-20 border-y border-slate-200/60">
          <div className="max-w-4xl mx-auto">
            <p className="text-violet-600 font-bold tracking-widest uppercase text-xs mb-3">
              LUK Verso
            </p>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4 tracking-tight">
              Inteligência de alcateia
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-4">
              O{' '}
              <a
                href="https://lukverso.lukos.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-violet-700 hover:text-violet-900 underline underline-offset-2"
              >
                LUK Verso
              </a>{' '}
              é o espaço onde a alcateia Lukos compartilha jeitos de usar o LUK — consultas e tarefas prontas para ensinar ao seu assistente. O conhecimento é de todos; os dados de nenhuma empresa.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              Você escolhe uma habilidade, cola a frase no seu LUK e ele mostra o que ela faz. Só aprende se você confirmar. Nada disso entrega dado de posto nenhum, nem dá poder novo ao LUK: a operação continua pedindo confirmação dentro do ERP.
            </p>
            <div className="relative w-full overflow-hidden rounded-3xl shadow-2xl shadow-slate-900/10 border border-slate-200 aspect-video bg-slate-900 mb-10">
              <iframe
                src="https://www.youtube.com/embed/pz0MW6eVqn0?rel=0&modestbranding=1"
                title="LUK Verso"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
            <ul className="space-y-3 text-slate-700 mb-10">
              <li className="flex items-start gap-3">
                <Check className="text-violet-600 shrink-0 mt-0.5" size={20} />
                <span>Habilidades da alcateia: o que perguntar e o que pedir no ERP, em linguagem natural.</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-violet-600 shrink-0 mt-0.5" size={20} />
                <span>Ensinar é copiar a frase, colar no seu LUK e confirmar. Sem cadastro de dado de cliente ou de posto.</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-violet-600 shrink-0 mt-0.5" size={20} />
                <span>O LUK do seu posto continua respeitando permissão e pedindo confirmação antes de alterar qualquer coisa.</span>
              </li>
            </ul>
            <a
              href="https://lukverso.lukos.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-violet-600 text-white px-8 py-3.5 rounded-full font-bold hover:bg-violet-700 transition-colors"
            >
              Abrir o LUK Verso <ExternalLink size={16} />
            </a>
          </div>
        </section>

        {/* Feature 2: Luk IA Interactive Demo */}
        <section className="les-section bg-slate-50 relative pt-20 pb-24 border-b border-slate-200/50 -mt-10">
          <div className="absolute top-10 left-1/2 -translate-x-1/2 bg-violet-600 text-white px-6 py-2 rounded-full font-bold text-xs tracking-widest uppercase shadow-lg shadow-violet-500/30 flex items-center gap-2 z-10">
            <Sparkles size={14} /> Análise Interativa
          </div>
          <div className="les-container relative z-0">
            <div className="les-feature-block reverse">
              <div className="les-content">
                <h3 className="les-title">
                  Sua operação mais inteligente com <span>Luk IA</span>
                </h3>
                <ul className="les-list">
                  <li className="les-item">
                    <Check className="les-icon" size={20} />
                    <span>Consulte produtos, estoque e cadastros do ERP em linguagem natural, dentro das permissões do usuário.</span>
                  </li>
                  <li className="les-item">
                    <Check className="les-icon" size={20} />
                    <span>Receba alertas inteligentes de ruptura antes que o estoque acabe e impacte suas vendas.</span>
                  </li>
                  <li className="les-item">
                    <Check className="les-icon" size={20} />
                    <span>Audite taxas de adquirentes e descubra cobranças indevidas com relatório pronto para contestar.</span>
                  </li>
                </ul>
                <Link to="/contato" className="les-cta">
                  Falar com especialista <ArrowRight size={18} />
                </Link>
              </div>

              <div className="les-visual">
                <LukIAMockup />
              </div>
            </div>
          </div>
        </section>

        <LukosAIFeatures />
        <LukosAIStrategy />
        
        <section className="py-20 bg-slate-50 text-center">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-4xl font-black text-slate-900 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>Pronto para transformar sua gestão?</h2>
            <Link to="/contato" className="inline-block bg-blue-600 text-white px-10 py-4 rounded-full font-bold text-lg shadow-xl hover:bg-blue-700 transition-all">
              Falar com especialista
            </Link>
          </div>
        </section>
      </motion.div>
    </div>
  );
};

export default LukosAIPage;
